/**
 * NotificationStore - IndexedDB wrapper for notification persistence
 * 
 * Provides persistent storage for real-time notifications with support for:
 * - Saving notifications
 * - Marking as read/unread
 * - Dismissing notifications
 * - Filtering by type, priority, read status
 * - Automatic cleanup of old notifications
 */

export interface StoredNotification {
  id: string
  userId: number
  businessId: number
  type: 'support-ticket' | 'license' | 'chat' | 'dashboard' | 'system'
  priority: 'low' | 'normal' | 'high' | 'critical'
  title: string
  message: string
  timestamp: string
  read: boolean
  dismissed: boolean
  actionUrl?: string
  metadata: Record<string, unknown>
  expiresAt: string
}

export interface NotificationFilter {
  type?: StoredNotification['type']
  priority?: StoredNotification['priority']
  read?: boolean
  dismissed?: boolean
}

const DB_NAME = 'co2impact_notifications'
const DB_VERSION = 1
const STORE_NAME = 'notifications'
const EXPIRY_DAYS = 30
const DISMISSED_EXPIRY_DAYS = 7

class NotificationStore {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null

  /**
   * Initialize the IndexedDB database
   */
  private async init(): Promise<void> {
    if (this.db) {
      return
    }

    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('[NotificationStore] Failed to open database', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        console.log('[NotificationStore] Database opened successfully')
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' })

          // Create indexes for efficient querying
          objectStore.createIndex('userId', 'userId', { unique: false })
          objectStore.createIndex('businessId', 'businessId', { unique: false })
          objectStore.createIndex('type', 'type', { unique: false })
          objectStore.createIndex('priority', 'priority', { unique: false })
          objectStore.createIndex('read', 'read', { unique: false })
          objectStore.createIndex('dismissed', 'dismissed', { unique: false })
          objectStore.createIndex('timestamp', 'timestamp', { unique: false })
          objectStore.createIndex('expiresAt', 'expiresAt', { unique: false })
          
          // Compound indexes for common queries
          objectStore.createIndex('userId_read', ['userId', 'read'], { unique: false })
          objectStore.createIndex('userId_dismissed', ['userId', 'dismissed'], { unique: false })
          objectStore.createIndex('userId_type', ['userId', 'type'], { unique: false })

          console.log('[NotificationStore] Object store created with indexes')
        }
      }
    })

    return this.initPromise
  }

  /**
   * Ensure database is initialized before operations
   */
  private async ensureDb(): Promise<IDBDatabase> {
    await this.init()
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    return this.db
  }

  /**
   * Save a notification to the store
   */
  async save(notification: StoredNotification): Promise<void> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      // Set expiry date if not provided
      if (!notification.expiresAt) {
        const expiryDate = new Date()
        expiryDate.setDate(expiryDate.getDate() + EXPIRY_DAYS)
        notification.expiresAt = expiryDate.toISOString()
      }

      const request = store.put(notification)

      request.onsuccess = () => {
        resolve()
      }

      request.onerror = () => {
        console.error('[NotificationStore] Failed to save notification', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<void> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      const getRequest = store.get(id)

      getRequest.onsuccess = () => {
        const notification = getRequest.result as StoredNotification | undefined

        if (!notification) {
          resolve() // Notification doesn't exist, nothing to do
          return
        }

        notification.read = true

        const putRequest = store.put(notification)

        putRequest.onsuccess = () => {
          resolve()
        }

        putRequest.onerror = () => {
          console.error('[NotificationStore] Failed to mark as read', putRequest.error)
          reject(putRequest.error)
        }
      }

      getRequest.onerror = () => {
        console.error('[NotificationStore] Failed to get notification', getRequest.error)
        reject(getRequest.error)
      }
    })
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId: number): Promise<void> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      
      let updateCount = 0

      // Set up transaction handlers FIRST
      transaction.oncomplete = () => {
        console.log(`[NotificationStore] Marked ${updateCount} notifications as read`)
        resolve()
      }

      transaction.onerror = () => {
        console.error('[NotificationStore] Transaction failed', transaction.error)
        reject(transaction.error)
      }

      transaction.onabort = () => {
        console.error('[NotificationStore] Transaction aborted')
        reject(new Error('Transaction aborted'))
      }

      // Use userId index instead of compound index (more reliable)
      const index = store.index('userId')
      const request = index.openCursor(IDBKeyRange.only(userId))

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor) {
          const notification = cursor.value as StoredNotification
          
          // Only update if not already read
          if (!notification.read) {
            notification.read = true

            const updateRequest = cursor.update(notification)

            updateRequest.onsuccess = () => {
              updateCount++
            }

            updateRequest.onerror = () => {
              console.error('[NotificationStore] Failed to update notification', updateRequest.error)
            }
          }

          cursor.continue()
        }
      }

      request.onerror = () => {
        console.error('[NotificationStore] Failed to mark all as read', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Dismiss a notification (soft delete)
   */
  async dismiss(id: string): Promise<void> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      const getRequest = store.get(id)

      getRequest.onsuccess = () => {
        const notification = getRequest.result as StoredNotification | undefined

        if (!notification) {
          resolve() // Notification doesn't exist, nothing to do
          return
        }

        notification.dismissed = true
        
        // Set shorter expiry for dismissed notifications
        const expiryDate = new Date()
        expiryDate.setDate(expiryDate.getDate() + DISMISSED_EXPIRY_DAYS)
        notification.expiresAt = expiryDate.toISOString()

        const putRequest = store.put(notification)

        putRequest.onsuccess = () => {
          resolve()
        }

        putRequest.onerror = () => {
          console.error('[NotificationStore] Failed to dismiss notification', putRequest.error)
          reject(putRequest.error)
        }
      }

      getRequest.onerror = () => {
        console.error('[NotificationStore] Failed to get notification', getRequest.error)
        reject(getRequest.error)
      }
    })
  }

  /**
   * Get all notifications for a user with optional filtering
   */
  async getAll(userId: number, filter?: NotificationFilter): Promise<StoredNotification[]> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('userId')

      const request = index.getAll(IDBKeyRange.only(userId))

      request.onsuccess = () => {
        let notifications = request.result as StoredNotification[]

        // Apply filters
        if (filter) {
          notifications = notifications.filter((notification) => {
            if (filter.type !== undefined && notification.type !== filter.type) {
              return false
            }
            if (filter.priority !== undefined && notification.priority !== filter.priority) {
              return false
            }
            if (filter.read !== undefined && notification.read !== filter.read) {
              return false
            }
            if (filter.dismissed !== undefined && notification.dismissed !== filter.dismissed) {
              return false
            }
            return true
          })
        }

        // Sort by timestamp (newest first)
        notifications.sort((a, b) => {
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        })

        resolve(notifications)
      }

      request.onerror = () => {
        console.error('[NotificationStore] Failed to get notifications', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Clean up expired notifications
   * Removes notifications older than 30 days and dismissed notifications older than 7 days
   */
  async cleanup(): Promise<number> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('expiresAt')

      const now = new Date().toISOString()
      const range = IDBKeyRange.upperBound(now)
      const request = index.openCursor(range)

      let deletedCount = 0

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor) {
          const notification = cursor.value as StoredNotification

          // Delete if expired
          if (notification.expiresAt <= now) {
            const deleteRequest = cursor.delete()

            deleteRequest.onsuccess = () => {
              deletedCount++
            }

            deleteRequest.onerror = () => {
              console.error('[NotificationStore] Failed to delete notification', deleteRequest.error)
            }
          }

          cursor.continue()
        } else {
          // All expired notifications processed
          console.log(`[NotificationStore] Cleaned up ${deletedCount} expired notifications`)
          resolve(deletedCount)
        }
      }

      request.onerror = () => {
        console.error('[NotificationStore] Failed to cleanup notifications', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Get count of unread notifications for a user
   */
  async getUnreadCount(userId: number): Promise<number> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('userId_read')

      // Query for unread notifications for this user
      const range = IDBKeyRange.only([userId, false])
      const request = index.count(range)

      request.onsuccess = () => {
        resolve(request.result)
      }

      request.onerror = () => {
        console.error('[NotificationStore] Failed to get unread count', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Clear all notifications for a user (useful when switching businesses)
   */
  async clearForUser(userId: number): Promise<void> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('userId')

      const request = index.openCursor(IDBKeyRange.only(userId))

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor) {
          const deleteRequest = cursor.delete()

          deleteRequest.onerror = () => {
            console.error('[NotificationStore] Failed to delete notification', deleteRequest.error)
          }

          cursor.continue()
        } else {
          // All notifications deleted
          resolve()
        }
      }

      request.onerror = () => {
        console.error('[NotificationStore] Failed to clear notifications', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Clear all notifications for a specific business
   * Used when switching businesses to remove old business context notifications
   */
  async clearBusinessNotifications(businessId: number): Promise<number> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('businessId')

      const request = index.openCursor(IDBKeyRange.only(businessId))

      let deletedCount = 0

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor) {
          const deleteRequest = cursor.delete()

          deleteRequest.onsuccess = () => {
            deletedCount++
          }

          deleteRequest.onerror = () => {
            console.error('[NotificationStore] Failed to delete notification', deleteRequest.error)
          }

          cursor.continue()
        } else {
          // All business notifications deleted
          console.log(`[NotificationStore] Cleared ${deletedCount} notifications for business ${businessId}`)
          resolve(deletedCount)
        }
      }

      request.onerror = () => {
        console.error('[NotificationStore] Failed to clear business notifications', request.error)
        reject(request.error)
      }
    })
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close()
      this.db = null
      this.initPromise = null
      console.log('[NotificationStore] Database closed')
    }
  }
}

// Export singleton instance
const notificationStore = new NotificationStore()

export default notificationStore
